import { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, X, BarChart3, Trash2, CheckCircle2, XCircle, Clock, Shield, ClipboardList, AlertTriangle } from "lucide-react";

const STATUS_LABELS = {
  active:   { label: "Actif",      cls: "bg-mint-100 text-mint-700 border-mint-200" },
  pending:  { label: "En attente", cls: "bg-sun-100 text-sun-700 border-sun-200" },
  closed:   { label: "Clos",       cls: "bg-cream-100 text-plum-700/60 border-cream-200" },
  rejected: { label: "Refusé",     cls: "bg-pink-100 text-pink-600 border-pink-200" },
};

const TABS = [
  { id: "pending", label: "À modérer",  icon: Shield },
  { id: "active",  label: "Actifs",     icon: CheckCircle2 },
  { id: "closed",  label: "Clos",       icon: Clock },
  { id: "all",     label: "Tous",       icon: ClipboardList },
];

export default function AdminSurveys() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [results, setResults] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/surveys?status_filter=all")
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const counts = useMemo(() => {
    const c = { pending: 0, active: 0, closed: 0, rejected: 0, all: items.length };
    items.forEach((s) => { const st = s.status || "active"; if (c[st] !== undefined) c[st] += 1; });
    return c;
  }, [items]);

  const filtered = useMemo(() => {
    if (tab === "all") return items;
    return items.filter((s) => (s.status || "active") === tab);
  }, [items, tab]);

  const openResults = async (s) => {
    setViewing(s);
    try {
      const { data } = await api.get(`/surveys/${s.id}/results`);
      setResults(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const approve = async (id) => {
    try { await api.post(`/admin/surveys/${id}/approve`); toast.success("Sondage approuvé et publié."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const reject = async (id) => {
    const reason = window.prompt("Raison du refus (optionnelle) :", "");
    if (reason === null) return;
    try { await api.post(`/admin/surveys/${id}/reject`, { reason }); toast.success("Sondage refusé."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Clore définitivement ce sondage ?")) return;
    try { await api.delete(`/surveys/${id}`); toast.success("Sondage clos."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="phenix-eyebrow mb-3">Consultations</div>
          <h1 className="heading-display text-2xl lg:text-3xl text-plum-900">
            <em className="text-pink-500">Sondages</em> — Modération & pilotage.
          </h1>
          <p className="text-[13px] text-plum-700/70 mt-2">
            {counts.pending} à modérer · {counts.active} actifs · {counts.closed} clos
          </p>
        </div>
        <Button
          onClick={() => setCreating(true)}
          className="btn-pill bg-plum-600 hover:bg-plum-700 text-white h-12 px-6"
          data-testid="admin-survey-create-btn">
          <Plus size={14} className="mr-2" /> Nouveau sondage
        </Button>
      </header>

      {/* Tabs */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-1" data-testid="admin-survey-tabs">
        {TABS.map((t) => {
          const active = tab === t.id;
          const count = counts[t.id] || 0;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-survey-tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-4 h-10 rounded-full text-[13px] font-bold border transition-colors whitespace-nowrap ${
                active
                  ? "bg-plum-700 text-white border-plum-700 shadow-md shadow-plum-700/15"
                  : "bg-white text-plum-700 border-cream-200 hover:bg-cream-50"
              }`}
            >
              <t.icon size={13} strokeWidth={2.4} />
              {t.label}
              <span className={`tabular-nums text-[11px] px-1.5 rounded-full ${active ? "bg-white/20" : "bg-cream-100 text-plum-700/60"}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Pending moderation banner */}
      {tab !== "pending" && counts.pending > 0 && (
        <button
          onClick={() => setTab("pending")}
          className="w-full flex items-center gap-3 rounded-2xl bg-sun-50 border-2 border-sun-300 px-4 py-3 hover:bg-sun-100 transition-colors text-left"
          data-testid="admin-survey-pending-banner"
        >
          <AlertTriangle size={20} className="text-sun-600 shrink-0" strokeWidth={2.4} />
          <div className="flex-1">
            <div className="font-display font-bold text-plum-900 text-[14px]">
              {counts.pending} sondage{counts.pending > 1 ? "s" : ""} en attente de modération
            </div>
            <div className="text-[12px] text-plum-700/70">Un commerçant attend votre validation</div>
          </div>
          <span className="text-plum-700 text-[12px] font-bold">Voir →</span>
        </button>
      )}

      {/* Surveys table */}
      {loading ? (
        <p className="text-plum-700/50 italic py-12 text-center">Chargement…</p>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-cream-200 p-16 text-center text-plum-700/50 italic rounded-2xl">
          Aucun sondage dans cette catégorie.
        </div>
      ) : (
        <SurveysTable
          surveys={filtered}
          showCreator
          showActions
          actions={{
            onResults: openResults,
            onApprove: approve,
            onReject: reject,
            onClose: remove,
          }}
        />
      )}

      <CreateSurveyDialog open={creating} onClose={() => setCreating(false)} onCreated={load} />
      <ResultsDialog open={!!viewing} survey={viewing} results={results}
        onClose={() => { setViewing(null); setResults(null); }} />
    </div>
  );
}

/** ============ Reusable surveys table ============ */
export function SurveysTable({ surveys, showCreator = false, showActions = false, actions = {} }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-cream-200 bg-white" data-testid="surveys-table">
      <table className="w-full text-[13px]">
        <thead className="bg-cream-50 text-[10px] uppercase tracking-wider text-plum-700/65 font-bold">
          <tr>
            <th className="text-left px-4 py-3">Titre</th>
            {showCreator && <th className="text-left px-3 py-3">Auteur</th>}
            <th className="text-left px-3 py-3">Statut</th>
            <th className="text-center px-3 py-3">Réponses</th>
            <th className="text-center px-3 py-3">Bons</th>
            <th className="text-left px-3 py-3 hidden md:table-cell">Créé le</th>
            {showActions && <th className="text-right px-3 py-3">Actions</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-cream-100">
          {surveys.map((s) => {
            const status = s.status || "active";
            const meta = STATUS_LABELS[status] || STATUS_LABELS.active;
            const date = s.created_at ? new Date(s.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "2-digit" }) : "—";
            return (
              <tr key={s.id} data-testid={`survey-row-${s.id}`} className="hover:bg-cream-50/60">
                <td className="px-4 py-3 align-top">
                  <div className="font-display font-bold text-plum-900 leading-snug">{s.title}</div>
                  <div className="text-[11px] text-plum-700/55 mt-0.5">{s.questions?.length || 0} question{(s.questions?.length || 0) > 1 ? "s" : ""} · cible : {s.target}</div>
                </td>
                {showCreator && (
                  <td className="px-3 py-3 align-top">
                    <div className="text-[12px] font-semibold text-plum-900">{s.creator_name || "—"}</div>
                    <div className="text-[10px] text-plum-700/55 uppercase tracking-wider">{s.creator_role || "admin"}</div>
                  </td>
                )}
                <td className="px-3 py-3 align-top">
                  <span className={`inline-flex items-center px-2.5 h-6 rounded-full border text-[11px] font-bold ${meta.cls}`}>
                    {meta.label}
                  </span>
                </td>
                <td className="px-3 py-3 text-center tabular-nums font-bold text-plum-900 align-top">
                  {s.response_count ?? 0}
                </td>
                <td className="px-3 py-3 text-center align-top">
                  {s.voucher_label ? (
                    <div className="inline-flex flex-col items-center gap-0.5">
                      <span className="font-bold text-pink-600 tabular-nums text-[12px]">
                        {(s.vouchers_remaining ?? s.vouchers_count ?? 0)}/{s.vouchers_count ?? 0}
                      </span>
                      <span className="text-[10px] text-plum-700/55 leading-tight max-w-[140px] truncate" title={s.voucher_label}>{s.voucher_label}</span>
                    </div>
                  ) : (
                    <span className="text-plum-700/30 text-[12px]">—</span>
                  )}
                </td>
                <td className="px-3 py-3 hidden md:table-cell text-plum-700/65 text-[12px] align-top">{date}</td>
                {showActions && (
                  <td className="px-3 py-3 text-right align-top">
                    <div className="inline-flex items-center gap-1">
                      {status === "pending" && actions.onApprove && (
                        <button onClick={() => actions.onApprove(s.id)} title="Approuver" data-testid={`survey-approve-${s.id}`}
                          className="w-8 h-8 rounded-lg bg-mint-100 text-mint-700 hover:bg-mint-200 grid place-items-center">
                          <CheckCircle2 size={14} />
                        </button>
                      )}
                      {status === "pending" && actions.onReject && (
                        <button onClick={() => actions.onReject(s.id)} title="Refuser" data-testid={`survey-reject-${s.id}`}
                          className="w-8 h-8 rounded-lg bg-pink-100 text-pink-600 hover:bg-pink-200 grid place-items-center">
                          <XCircle size={14} />
                        </button>
                      )}
                      {actions.onResults && (
                        <button onClick={() => actions.onResults(s)} title="Résultats" data-testid={`survey-results-${s.id}`}
                          className="w-8 h-8 rounded-lg bg-plum-100 text-plum-700 hover:bg-plum-200 grid place-items-center">
                          <BarChart3 size={14} />
                        </button>
                      )}
                      {status === "active" && actions.onClose && (
                        <button onClick={() => actions.onClose(s.id)} title="Clore" data-testid={`survey-delete-${s.id}`}
                          className="w-8 h-8 rounded-lg bg-cream-100 text-plum-700/70 hover:bg-pink-100 hover:text-pink-600 grid place-items-center">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CreateSurveyDialog({ open, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [target, setTarget] = useState("tous");
  const [questions, setQuestions] = useState([{ question: "", options: ["", ""] }]);
  const [vouchersCount, setVouchersCount] = useState("");
  const [voucherLabel, setVoucherLabel] = useState("");
  const [voucherPercent, setVoucherPercent] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setTitle(""); setDescription(""); setTarget("tous");
    setQuestions([{ question: "", options: ["", ""] }]);
    setVouchersCount(""); setVoucherLabel(""); setVoucherPercent("");
  };

  const addQuestion = () => setQuestions([...questions, { question: "", options: ["", ""] }]);
  const removeQuestion = (qi) => setQuestions(questions.filter((_, i) => i !== qi));
  const updateQ = (qi, field, val) => { const next = [...questions]; next[qi][field] = val; setQuestions(next); };
  const updateOpt = (qi, oi, val) => { const next = [...questions]; next[qi].options[oi] = val; setQuestions(next); };
  const addOpt = (qi) => { const next = [...questions]; next[qi].options.push(""); setQuestions(next); };
  const removeOpt = (qi, oi) => { const next = [...questions]; if (next[qi].options.length > 2) { next[qi].options.splice(oi, 1); setQuestions(next); } };

  const submit = async () => {
    if (!title.trim() || !description.trim()) return toast.error("Titre et description requis");
    for (const q of questions) {
      if (!q.question.trim()) return toast.error("Toutes les questions doivent être remplies");
      if (q.options.some((o) => !o.trim())) return toast.error("Toutes les options doivent être remplies");
    }
    setBusy(true);
    try {
      await api.post("/surveys", {
        title, description, target, questions,
        vouchers_count: Number(vouchersCount) || 0,
        voucher_label: voucherLabel || null,
        voucher_percent: Number(voucherPercent) || 0,
      });
      toast.success("Sondage publié.");
      reset(); onClose(); onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-cream-50 border-cream-200 rounded-xl">
        <DialogHeader>
          <DialogTitle className="heading-display text-lg text-plum-900">
            <em className="text-pink-500">Nouveau</em> sondage
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <div className="space-y-2">
            <Label className="text-[11px] uppercase tracking-[0.18em] text-plum-700/70">Titre</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="new-survey-title" className="rounded-xl border-cream-200 bg-transparent h-11" />
          </div>
          <div className="space-y-2">
            <Label className="text-[11px] uppercase tracking-[0.18em] text-plum-700/70">Description</Label>
            <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="new-survey-desc" className="rounded-xl border-cream-200 bg-transparent" />
          </div>
          <div className="space-y-2">
            <Label className="text-[11px] uppercase tracking-[0.18em] text-plum-700/70">Public cible</Label>
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger data-testid="new-survey-target" className="rounded-xl border-cream-200 bg-transparent h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tous">Tous</SelectItem>
                <SelectItem value="habitants">Habitants</SelectItem>
                <SelectItem value="commercants">Commerçants</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-[11px] uppercase tracking-[0.18em] text-plum-700/70">Bons à gagner</Label>
              <Input type="number" min="0" max="1000" value={vouchersCount}
                onChange={(e) => setVouchersCount(e.target.value)} data-testid="new-survey-vouchers-count"
                placeholder="0" className="rounded-xl border-cream-200 bg-transparent h-11" />
            </div>
            <div className="space-y-2">
              <Label className="text-[11px] uppercase tracking-[0.18em] text-plum-700/70">% de réduction</Label>
              <Input type="number" min="0" max="100" value={voucherPercent}
                onChange={(e) => setVoucherPercent(e.target.value)} data-testid="new-survey-voucher-percent"
                placeholder="0" className="rounded-xl border-cream-200 bg-transparent h-11" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-[11px] uppercase tracking-[0.18em] text-plum-700/70">Libellé du bon</Label>
            <Input value={voucherLabel} onChange={(e) => setVoucherLabel(e.target.value)}
              data-testid="new-survey-voucher-label"
              placeholder="ex: -10 % chez la Bijouterie Éclat"
              className="rounded-xl border-cream-200 bg-transparent h-11" />
          </div>
          <div className="space-y-3 pt-3 border-t border-cream-200">
            <div className="flex items-center justify-between">
              <Label className="text-[11px] uppercase tracking-[0.18em] text-plum-700/70">Questions</Label>
              <button onClick={addQuestion} className="btn-ghost-line text-xs"><Plus size={12} /> Ajouter</button>
            </div>
            {questions.map((q, qi) => (
              <div key={qi} className="border border-cream-200 p-4 space-y-3 bg-cream-25 rounded-xl">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[11px] text-plum-700/50">{String(qi + 1).padStart(2, "0")}</span>
                  <Input value={q.question} onChange={(e) => updateQ(qi, "question", e.target.value)}
                    placeholder={`Question ${qi + 1}`} data-testid={`new-survey-q-${qi}`}
                    className="rounded-xl border-cream-200 bg-transparent h-10" />
                  {questions.length > 1 && (
                    <button onClick={() => removeQuestion(qi)} className="text-plum-700/50 hover:text-pink-500"><X size={14} /></button>
                  )}
                </div>
                <div className="space-y-2 pl-7">
                  {q.options.map((o, oi) => (
                    <div key={oi} className="flex items-center gap-2">
                      <span className="font-mono text-[11px] text-plum-700/50 w-5">{String.fromCharCode(65 + oi)}</span>
                      <Input value={o} onChange={(e) => updateOpt(qi, oi, e.target.value)}
                        placeholder={`Option ${oi + 1}`} data-testid={`new-survey-q${qi}-opt${oi}`}
                        className="rounded-xl border-cream-200 bg-transparent h-9" />
                      {q.options.length > 2 && (
                        <button onClick={() => removeOpt(qi, oi)} className="text-plum-700/50 hover:text-pink-500"><X size={12} /></button>
                      )}
                    </div>
                  ))}
                  <button onClick={() => addOpt(qi)} className="btn-ghost-line text-[11px] ml-7"><Plus size={10} /> Option</button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-xl">Annuler</Button>
          <Button onClick={submit} disabled={busy} className="btn-pill bg-plum-600 hover:bg-plum-700 text-white" data-testid="new-survey-submit">
            Publier
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResultsDialog({ open, survey, results, onClose }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-cream-50 border-cream-200 rounded-xl">
        <DialogHeader>
          <DialogTitle className="heading-display text-lg text-plum-900">{survey?.title}</DialogTitle>
        </DialogHeader>
        {!results ? (
          <p className="text-plum-700/50 italic">Chargement...</p>
        ) : results.total_responses === 0 ? (
          <p className="text-plum-700/50 text-center py-6 italic">Aucune réponse pour le moment.</p>
        ) : (
          <div className="space-y-8">
            <div className="border-b border-cream-200 pb-4">
              <div className="phenix-eyebrow mb-2">Réponses</div>
              <div className="font-display text-4xl text-ink-900">
                {results.total_responses}<span className="text-pink-500 text-xl font-light"> au total</span>
              </div>
            </div>
            {results.results.map((r, ri) => {
              const total = r.counts.reduce((a, b) => a + b, 0) || 1;
              return (
                <div key={ri}>
                  <div className="font-display text-lg text-ink-900 mb-4">
                    {String(ri + 1).padStart(2, "0")}. {r.question}
                  </div>
                  <div className="space-y-3">
                    {r.options.map((opt, oi) => {
                      const pct = Math.round((r.counts[oi] / total) * 100);
                      return (
                        <div key={oi}>
                          <div className="flex items-center justify-between text-sm mb-1.5">
                            <span className="text-plum-900">{opt}</span>
                            <span className="font-mono text-xs text-plum-700/70 tabular-nums">{r.counts[oi]} · {pct}%</span>
                          </div>
                          <div className="h-1.5 bg-cream-100 rounded-full overflow-hidden">
                            <div className="h-full bg-plum-600 rounded-full" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
